import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef
} from "react";
import {
  getJobLinksSection,
  getHeadTableItems,
  getJobs,
  getResults,
  getAdmitCards,
  getAnswerKeys,
  getSyllabuses,
  getAdmissions,
  getEligibilityCriterias,
  getHomepageSingleColumnSection,
  getContacts,
  getFeedbacks,
  API
} from "../api/api";

const GlobalDataContext = createContext();

export function useGlobalData() {
  return useContext(GlobalDataContext);
}

// Cache TTL: 60 sec (repeat navigation par unnecessary refetch avoid karega)
const CACHE_TTL_MS = 60 * 1000;
const HOME_THREE_TABLE_LIMIT = 30;
const THREE_TABLE_SECTIONS = ["jobs", "results", "admitCards", "answerKeys", "syllabuses", "admissions", "selectionjobs"];

const createThreeTableSectionMap = (value = false) => ({
  jobs: value,
  results: value,
  admitCards: value,
  answerKeys: value,
  syllabuses: value,
  admissions: value
});

export function GlobalDataProvider({ children }) {
  const [profile, setProfile] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [error, setError] = useState("");

  const [jobLinks, setJobLinks] = useState([]);
  const [headTable, setHeadTable] = useState([]);
  const [threeTable, setThreeTable] = useState({
    jobs: [],
    results: [],
    admitCards: [],
    answerKeys: [],
    syllabuses: [],
    admissions: [],
    selectionjobs: []
  });
  // Infinite scroll: offset and hasMore per section
  const [threeTableOffset, setThreeTableOffset] = useState({
    jobs: 0,
    results: 0,
    admitCards: 0,
    answerKeys: 0,
    syllabuses: 0,
    admissions: 0
  });
  const [threeTableHasMore, setThreeTableHasMore] = useState({
    jobs: true,
    results: true,
    admitCards: true,
    answerKeys: true,
    syllabuses: true,
    admissions: true
  }); // used in ThreeTableSection
  const [homepageSingleColumn, setHomepageSingleColumn] = useState([]);
  const [faqs, setFaqs] = useState([]);
  const [eligibilityCriterias, setEligibilityCriterias] = useState([]);

  // Global loading (public skeleton ke liye)
  const [loading, setLoading] = useState(false);

  // Contacts / Feedback ke liye dedicated states
  const [contacts, setContacts] = useState([]);
  const [contactsLoading, setContactsLoading] = useState(false);
  const [contactsLoaded, setContactsLoaded] = useState(false);

  const [feedbacks, setFeedbacks] = useState([]);
  const [feedbacksLoading, setFeedbacksLoading] = useState(false);
  const [feedbacksLoaded, setFeedbacksLoaded] = useState(false);

  
  // ✅ NEW: notifications loading/cache flags
  const [notificationsLoading, setNotificationsLoading] = useState(false);
  const [notificationsLoaded, setNotificationsLoaded] = useState(false);


  // ---- Global smart fetch infra ----
  // inFlightRef: same key ka parallel duplicate request रोकता है
  // cacheTimeRef: last success time store karta hai
  // loadingCountRef: multiple parallel calls me loading flicker रोकता है
  const inFlightRef = useRef(new Map());
  const cacheTimeRef = useRef(new Map());
  const loadingCountRef = useRef(0);
  const threeTableBackgroundAbortRef = useRef(null);
  const threeTableFullSectionLoadedRef = useRef(createThreeTableSectionMap(false));

  const isRequestCanceled = useCallback((err) => {
    return err?.name === "CanceledError" || err?.code === "ERR_CANCELED";
  }, []);

  const cancelThreeTableBackgroundRefresh = useCallback(() => {
    if (threeTableBackgroundAbortRef.current) {
      threeTableBackgroundAbortRef.current.abort();
      threeTableBackgroundAbortRef.current = null;
    }
  }, []);

  const markThreeTableSectionsLoaded = useCallback((sections, loaded) => {
    sections.forEach((section) => {
      if (section in threeTableFullSectionLoadedRef.current) {
        threeTableFullSectionLoadedRef.current[section] = loaded;
      }
    });
  }, []);

  const getPendingThreeTableSections = useCallback(() => {
    return THREE_TABLE_SECTIONS.filter(
      (section) => !threeTableFullSectionLoadedRef.current[section]
    );
  }, []);

  const isThreeTableSectionFullyLoaded = useCallback((section) => {
    return Boolean(threeTableFullSectionLoadedRef.current?.[section]);
  }, []);

  const startLoading = useCallback(() => {
    loadingCountRef.current += 1;
    setLoading(true);
  }, []);

  const stopLoading = useCallback(() => {
    loadingCountRef.current = Math.max(0, loadingCountRef.current - 1);
    if (loadingCountRef.current === 0) setLoading(false);
  }, []);

  const runSmartFetch = useCallback(
    async ({ key, force = false, hasData, fetcher, ttl = CACHE_TTL_MS, showLoading = true }) => {
      const now = Date.now();
      const lastFetch = cacheTimeRef.current.get(key) || 0;
      const isFresh = now - lastFetch < ttl;

      // force = false + data available + cache fresh => skip request
      if (!force && hasData?.() && isFresh) return;

      // agar same key ka request already chal raha hai to wahi promise return
      if (inFlightRef.current.has(key)) {
        return inFlightRef.current.get(key);
      }

      if (showLoading) startLoading();

      const reqPromise = (async () => {
        try {
          await fetcher();
          cacheTimeRef.current.set(key, Date.now());
        } finally {
          inFlightRef.current.delete(key);
          if (showLoading) stopLoading();
        }
      })();

      inFlightRef.current.set(key, reqPromise);
      return reqPromise;
    },
    [startLoading, stopLoading]
  );

  // ---- On-demand fetchers ----
  // sabme force param add hai:
  // - false: cache guard
  // - true: hard refresh
  const fetchJobLinks = useCallback(
    async (force = false) =>
      runSmartFetch({
        key: "jobLinks",
        force,
        hasData: () => (jobLinks?.length || 0) > 0,
        fetcher: async () => {
          try {
            const res = await getJobLinksSection();
            setJobLinks(res.data?.rows || []);
          } catch {
            setJobLinks([]);
          }
        }
      }),
    [runSmartFetch, jobLinks?.length]
  );

  const fetchHeadTable = useCallback(
    async (force = false) =>
      runSmartFetch({
        key: "headTable",
        force,
        hasData: () => (headTable?.length || 0) > 0,
        fetcher: async () => {
          try {
            const res = await getHeadTableItems();
            setHeadTable(res.data || []);
          } catch {
            setHeadTable([]);
          }
        }
      }),
    [runSmartFetch, headTable?.length]
  );

  const fetchThreeTable = useCallback(
    async (force = false, options = {}) =>
      runSmartFetch({
        key: "threeTable",
        force,
        showLoading: options.showLoading !== false,
        limit: options.limit,
        hasData: () =>
          (threeTable?.jobs?.length || 0) > 0 ||
          (threeTable?.results?.length || 0) > 0 ||
          (threeTable?.admitCards?.length || 0) > 0 ||
          (threeTable?.answerKeys?.length || 0) > 0 ||
          (threeTable?.syllabuses?.length || 0) > 0 ||
          (threeTable?.admissions?.length || 0) > 0,
        fetcher: async () => {
          const requestSignal = options?.signal;
          const limit = options.limit;
          const sectionsToFetch = options.onlyPendingSections
            ? getPendingThreeTableSections()
            : [...THREE_TABLE_SECTIONS];

          if (sectionsToFetch.length === 0) return;

          for (const section of sectionsToFetch) {
            try {
              switch (section) {
                case "jobs": {
                  const jobData = await getJobs({ signal: requestSignal, skipLoader: options.skipLoader, limit });
                  setThreeTable((prev) => ({ ...prev, jobs: jobData.data || [] }));
                  break;
                }
                case "results": {
                  const resultData = await getResults( { signal: requestSignal, skipLoader: options.skipLoader, limit });
                  const sortedResults = (resultData.data || []).slice();
                  setThreeTable((prev) => ({ ...prev, results: sortedResults }));
                  break;
                }
                case "admitCards": {
                  const admitCardData = await getAdmitCards({ signal: requestSignal, skipLoader: options.skipLoader, limit });
                  const sortedAdmitCards = (admitCardData.data || []).slice();
                  setThreeTable((prev) => ({ ...prev, admitCards: sortedAdmitCards }));
                  break;
                }
                case "answerKeys": {
                  // Remove status filter for admin: get all answer keys
                  const answerKeyData = await getAnswerKeys({ signal: requestSignal, skipLoader: options.skipLoader, limit });
                  const sortedAnswerKeys = (answerKeyData.data || []).slice();
                  setThreeTable((prev) => ({ ...prev, answerKeys: sortedAnswerKeys }));
                  break;
                }
                case "syllabuses": {
                  const syllabusData = await getSyllabuses( { signal: requestSignal, skipLoader: options.skipLoader, limit });
                  const sortedSyllabuses = (syllabusData.data || []).slice();
                  setThreeTable((prev) => ({ ...prev, syllabuses: sortedSyllabuses }));
                  break;
                }
                case "admissions": {
                  const admissionData = await getAdmissions( { signal: requestSignal, skipLoader: options.skipLoader, limit });
                  const sortedAdmissions = (admissionData.data || []);
                  setThreeTable((prev) => ({ ...prev, admissions: sortedAdmissions }));
                  break;
                }
                default:
                  break;
              }

              markThreeTableSectionsLoaded([section], true);
              cacheTimeRef.current.set(`threeTable:${section}`, Date.now());
            } catch (err) {
              if (isRequestCanceled(err)) throw err;

              markThreeTableSectionsLoaded([section], false);

              // Background refresh fail hone par existing preview data preserve rakho.
              if (options.showLoading === false) continue;

              setThreeTable((prev) => ({ ...prev, [section]: [] }));
            }
          }
        }
      }),
    [
      isRequestCanceled,
      runSmartFetch,
      getPendingThreeTableSections,
      markThreeTableSectionsLoaded,
      threeTable?.jobs?.length,
      threeTable?.results?.length,
      threeTable?.admitCards?.length,
      threeTable?.answerKeys?.length,
      threeTable?.syllabuses?.length,
      threeTable?.admissions?.length
    ]
  );

  const fetchThreeTablePreview = useCallback(
    async (force = false, limit = HOME_THREE_TABLE_LIMIT, options = {}) =>
      runSmartFetch({
        key: `threeTable:preview:${limit}`,
        force,
        hasData: () =>
          (threeTable?.jobs?.length || 0) > 0 ||
          (threeTable?.results?.length || 0) > 0 ||
          (threeTable?.admitCards?.length || 0) > 0 ||
          (threeTable?.answerKeys?.length || 0) > 0 ||
          (threeTable?.syllabuses?.length || 0) > 0 ||
          (threeTable?.admissions?.length || 0) > 0,
        fetcher: async () => {
          try {
            const requestSignal = options?.signal;

            const jobData = await getJobs({ limit, signal: requestSignal, skipLoader: options.skipLoader });
            const sortedJobs = jobData.data || [];

            const resultData = await getResults("approved", { limit, signal: requestSignal, skipLoader: options.skipLoader });
            const sortedResults = (resultData.data || []).slice();

            const admitCardData = await getAdmitCards({ status: "approved", limit, signal: requestSignal, skipLoader: options.skipLoader });
            const sortedAdmitCards = (admitCardData.data || [])
              .slice()
              .reverse();

            const answerKeyData = await getAnswerKeys("approved", { limit, signal: requestSignal, skipLoader: options.skipLoader });
            const sortedAnswerKeys = (answerKeyData.data || [])
              .slice()
              .reverse();

            const syllabusData = await getSyllabuses("approved", { limit, signal: requestSignal, skipLoader: options.skipLoader });
            const sortedSyllabuses = (syllabusData.data || [])
              .slice()
              .reverse();

            const admissionData = await getAdmissions("approved", { limit, signal: requestSignal, skipLoader: options.skipLoader });
            const sortedAdmissions = (admissionData.data || []);

            setThreeTable({
              jobs: sortedJobs,
              results: sortedResults,
              admitCards: sortedAdmitCards,
              answerKeys: sortedAnswerKeys,
              syllabuses: sortedSyllabuses,
              admissions: sortedAdmissions
            });

            // Preview limited data hai, full load status reset rakho.
            markThreeTableSectionsLoaded(THREE_TABLE_SECTIONS, false);

            // Preview data ko full section cache na maano, taaki section pages full fetch kar saken.
            THREE_TABLE_SECTIONS.forEach(
              (section) => cacheTimeRef.current.delete(`threeTable:${section}`)
            );
          } catch (err) {
            if (isRequestCanceled(err)) throw err;

            setThreeTable({
              jobs: [],
              results: [],
              admitCards: [],
              answerKeys: [],
              syllabuses: [],
              admissions: []
            });

            markThreeTableSectionsLoaded(THREE_TABLE_SECTIONS, false);
          }
        }
      }),
    [
      markThreeTableSectionsLoaded,
      isRequestCanceled,
      runSmartFetch,
      threeTable?.jobs?.length,
      threeTable?.results?.length,
      threeTable?.admitCards?.length,
      threeTable?.answerKeys?.length,
      threeTable?.syllabuses?.length,
      threeTable?.admissions?.length
    ]
  );

  const fetchThreeTableSection = useCallback(
    async (section, force = false, options = {}) => {
      const sectionKey = `threeTable:${section}`;
      const limit = options.limit || HOME_THREE_TABLE_LIMIT;
      const offset = options.offset ?? threeTableOffset[section] ?? 0;

      const hasSectionData = () => {
        switch (section) {
          case "jobs":
            return (threeTable?.jobs?.length || 0) > 0;
          case "results":
            return (threeTable?.results?.length || 0) > 0;
          case "admitCards":
            return (threeTable?.admitCards?.length || 0) > 0;
          case "answerKeys":
            return (threeTable?.answerKeys?.length || 0) > 0;
          case "syllabuses":
            return (threeTable?.syllabuses?.length || 0) > 0;
          case "admissions":
            return (threeTable?.admissions?.length || 0) > 0;
          case "selectionjobs":
            return (threeTable?.selectionjobs?.length || 0) > 0;
          default:
            return false;
        }
      };

      const isSectionFullyLoaded = () => {
        return Boolean(threeTableFullSectionLoadedRef.current?.[section]);
      };

      // Table-wise pages ke liye: global state me data hai to API call mat karo.
      if (
        !force &&
        options.preferLoadedData &&
        hasSectionData() &&
        !(options.fetchIfPartial && !isSectionFullyLoaded())
      ) {
        return;
      }

      return runSmartFetch({
        key: sectionKey + ":" + offset,
        force,
        showLoading: options.showLoading !== false,
        hasData: () => {
          if (options.fetchIfPartial) {
            return hasSectionData() && isSectionFullyLoaded();
          }
          return hasSectionData();
        },
        fetcher: async () => {
          try {
            let data = [];
            switch (section) {
              case "jobs": {
                const jobData = await getJobs({ limit, offset });
                data = jobData.data || [];
                break;
              }
              case "results": {
                const resultData = await getResults({ limit, offset });
                data = (resultData.data || []).slice();
                break;
              }
              case "admitCards": {
                const admitCardData = await getAdmitCards({ limit, offset });
                data = (admitCardData.data || []).slice();
                break;
              }
              case "answerKeys": {
                const answerKeyData = await getAnswerKeys({ limit, offset });
                data = (answerKeyData.data || []).slice();
                break;
              }
              case "syllabuses": {
                const syllabusData = await getSyllabuses({ limit, offset });
                data = (syllabusData.data || []).slice();
                break;
              }
              case "admissions": {
                const admissionData = await getAdmissions({ limit, offset });
                data = (admissionData.data || []);
                break;
              }
              case "selectionjobs": {
                // Add this case for selectionjobs
                const selectionJobData = await API.get("/job-selections", { params: { limit, offset } });
                data = selectionJobData.data?.rows || selectionJobData.data || [];
                break;
              }
              default:
                break;
            }
            setThreeTable((prev) => ({
              ...prev,
              [section]: offset > 0 ? [...(prev[section] || []), ...data] : data
            }));
            setThreeTableOffset((prev) => ({ ...prev, [section]: offset + data.length }));
            setThreeTableHasMore((prev) => ({ ...prev, [section]: data.length === limit }));
            if (data.length < limit) markThreeTableSectionsLoaded([section], true);
          } catch {
            markThreeTableSectionsLoaded([section], false);
            setThreeTableHasMore((prev) => ({ ...prev, [section]: false }));
            if (options.showLoading === false) return;
            setThreeTable((prev) => ({ ...prev, [section]: [] }));
          }
        }
      });
    },
    [
      markThreeTableSectionsLoaded,
      runSmartFetch,
      threeTable,
      threeTableOffset,
      setThreeTableOffset,
      setThreeTableHasMore
    ]
  );

  const fetchEligibilityCriterias = useCallback(
    async (force = false) =>
      runSmartFetch({
        key: "eligibilityCriterias",
        force,
        hasData: () => (eligibilityCriterias?.length || 0) > 0,
        fetcher: async () => {
          try {
            const res = await getEligibilityCriterias();
            setEligibilityCriterias(res.data || []);
          } catch {
            setEligibilityCriterias([]);
          }
        }
      }),
    [runSmartFetch, eligibilityCriterias?.length]
  );

  const fetchHomepageSingleColumn = useCallback(
    async (force = false) =>
      runSmartFetch({
        key: "homepageSingleColumn",
        force,
        hasData: () => (homepageSingleColumn?.length || 0) > 0,
        fetcher: async () => {
          try {
            const res = await getHomepageSingleColumnSection();
            setHomepageSingleColumn(res.data?.items || []);
          } catch {
            setHomepageSingleColumn([]);
          }
        }
      }),
    [runSmartFetch, homepageSingleColumn?.length]
  );

  const fetchFaqs = useCallback(
    async (force = false) =>
      runSmartFetch({
        key: "faqs",
        force,
        hasData: () => (faqs?.length || 0) > 0,
        fetcher: async () => {
          try {
            const res = await API.get("/faq");
            setFaqs(Array.isArray(res.data) ? res.data : []);
          } catch {
            setFaqs([]);
          }
        }
      }),
    [runSmartFetch, faqs?.length]
  );

  // Contacts/Feedback ke liye existing loaded-flag strategy + force refresh
  const fetchFeedbacks = useCallback(
    async (force = false) => {
      if (!force && (feedbacksLoading || feedbacksLoaded)) return;
      try {
        setFeedbacksLoading(true);
        const res = await getFeedbacks();
        const list = Array.isArray(res?.data)
          ? res.data
          : res?.data?.feedbacks || [];
        setFeedbacks(list);
        setFeedbacksLoaded(true);
      } catch {
        setFeedbacks([]);
        setFeedbacksLoaded(true);
      } finally {
        setFeedbacksLoading(false);
      }
    },
    [feedbacksLoading, feedbacksLoaded]
  );

  const fetchContacts = useCallback(
    async (force = false) => {
      if (!force && (contactsLoading || contactsLoaded)) return;
      try {
        setContactsLoading(true);
        const res = await getContacts();
        const list = Array.isArray(res?.data)
          ? res.data
          : res?.data?.contacts || [];
        setContacts(list);
        setContactsLoaded(true);
      } catch {
        setContacts([]);
        setContactsLoaded(true);
      } finally {
        setContactsLoading(false);
      }
    },
    [contactsLoading, contactsLoaded]
  );

   // ✅ NEW: notifications fetch (repeat mount par baar-baar call nahi hoga)
  const fetchNotifications = useCallback(
    async (force = false) => {
      if (!force && (notificationsLoading || notificationsLoaded)) return;
      try {
        setNotificationsLoading(true);
        const res = await API.get("/notifications");
        const list = Array.isArray(res?.data)
          ? res.data
          : res?.data?.notifications || [];
        setNotifications(list);
        setNotificationsLoaded(true);
      } catch {
        setNotifications([]);
        setNotificationsLoaded(true);
      } finally {
        setNotificationsLoading(false);
      }
    },
    [notificationsLoading, notificationsLoaded]
  );

  // Public pages ke liye one-call hydrate helper
  const hydratePublicData = useCallback(
    async (force = false) => {
      await Promise.all([
        fetchJobLinks(force),
        fetchHeadTable(force),
        fetchThreeTablePreview(force),
        fetchHomepageSingleColumn(force),
      ]);

      // Preview render ke turant baad full data background me load karo, bina skeleton flash ke.
      cancelThreeTableBackgroundRefresh();
      const controller = new AbortController();
      threeTableBackgroundAbortRef.current = controller;

      fetchThreeTable(true, {
        showLoading: false,
        signal: controller.signal,
        skipLoader: true,
        onlyPendingSections: true
      })
        .catch(() => {})
        .finally(() => {
          if (threeTableBackgroundAbortRef.current === controller) {
            threeTableBackgroundAbortRef.current = null;
          }
        });
    },
    [fetchJobLinks, fetchHeadTable, fetchThreeTablePreview, fetchHomepageSingleColumn, fetchThreeTable, cancelThreeTableBackgroundRefresh]
  );

  // dataChanged => only targeted refresh (force true)
  useEffect(() => {
    const handler = (e) => {
      const resource = e?.detail?.resource;
      switch (resource) {
        case "contacts":
          fetchContacts(true);
          break;
        case "feedback":
        case "feedbacks":
          fetchFeedbacks(true);
          break;
        case "jobLinks":
          fetchJobLinks(true);
          break;
        case "headTable":
          fetchHeadTable(true);
          break;
        case "threeTable":
          fetchThreeTable(true);
          break;
        case "eligibility":
          fetchEligibilityCriterias(true);
          break;
        case "homepageSingleColumn":
          fetchHomepageSingleColumn(true);
          break;
        case "faq":
          fetchFaqs(true);
          break;
        case "notifications": // ✅ NEW
          fetchNotifications(true);
          break;
        default:
          break;
      }
    };

    window.addEventListener("dataChanged", handler);
    return () => window.removeEventListener("dataChanged", handler);
  }, [
    fetchContacts,
    fetchFeedbacks,
    fetchJobLinks,
    fetchHeadTable,
    fetchThreeTable,
    fetchEligibilityCriterias,
    fetchHomepageSingleColumn,
    fetchFaqs,
    fetchNotifications // ✅ NEW
  ]);

  return (
    <GlobalDataContext.Provider
      value={{
        profile,
        setProfile,
        notifications,
        setNotifications,
        error,
        setError,
        loading,

        // ✅ NEW exports
        notificationsLoading,
        notificationsLoaded,
        fetchNotifications,

        jobLinks,
        setJobLinks,
        fetchJobLinks,

        headTable,
        setHeadTable,
        fetchHeadTable,

        threeTable,
        setThreeTable,
        fetchThreeTable,
        fetchThreeTablePreview,
        cancelThreeTableBackgroundRefresh,
        isThreeTableSectionFullyLoaded,
        fetchThreeTableSection,
        threeTableHasMore,

        eligibilityCriterias,
        setEligibilityCriterias,
        fetchEligibilityCriterias,

        homepageSingleColumn,
        setHomepageSingleColumn,
        fetchHomepageSingleColumn,

        faqs,
        setFaqs,
        fetchFaqs,

        contacts,
        setContacts,
        contactsLoading,
        fetchContacts,

        feedbacks,
        setFeedbacks,
        feedbacksLoading,
        fetchFeedbacks,

        hydratePublicData
      }}
    >
      {children}
    </GlobalDataContext.Provider>
  );
}